import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  FormGroup,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Restore as RestoreIcon,
  Agriculture as AgricultureIcon,
} from '@mui/icons-material';

const GEOMETRY_KINDS = ['Point', 'LineString', 'Polygon'] as const;
type GeometryKind = typeof GEOMETRY_KINDS[number];

interface OperationMethodOption {
  id: string;
  name: string;
  archivedDate?: string;
}

interface OperationTypeItem {
  id: string;
  name: string;
  alias?: string;
  sortIndex: number;
  defaultGeometryKind: string;
  allowedGeometryKinds: string[];
  defaultColour?: string;
  dateCreated: string;
  archivedDate?: string;
  methodIds: string[];
}

interface FormState {
  name: string;
  alias: string;
  sortIndex: string;
  defaultGeometryKind: GeometryKind;
  allowedGeometryKinds: GeometryKind[];
  defaultColour: string;
  methodIds: string[];
}

const emptyForm: FormState = {
  name: '',
  alias: '',
  sortIndex: '100',
  defaultGeometryKind: 'LineString',
  allowedGeometryKinds: ['LineString', 'Polygon'],
  defaultColour: '#8d6e63',
  methodIds: [],
};

const OperationTypeManagement: React.FC = () => {
  const auth = useAuth();
  const [items, setItems] = useState<OperationTypeItem[]>([]);
  const [methods, setMethods] = useState<OperationMethodOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<OperationTypeItem | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [typesRes, methodsRes] = await Promise.all([
        fetch(`/api/operation-types?includeArchived=${includeArchived}`, { headers: authHeaders() }),
        fetch('/api/operation-methods', { headers: authHeaders() }),
      ]);
      if (!typesRes.ok) throw new Error('Failed to fetch operation types');
      if (!methodsRes.ok) throw new Error('Failed to fetch operation methods');
      setItems(await typesRes.json());
      setMethods(await methodsRes.json());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [includeArchived, authHeaders]);

  useEffect(() => {
    if (auth.isSignedIn && auth.tokens?.accessToken) {
      fetchAll();
    }
  }, [fetchAll, auth.isSignedIn, auth.tokens?.accessToken]);

  const methodName = (id: string) => methods.find((m) => m.id === id)?.name ?? id;

  const handleOpenDialog = (item?: OperationTypeItem) => {
    if (item) {
      setEditingItem(item);
      setForm({
        name: item.name,
        alias: item.alias ?? '',
        sortIndex: String(item.sortIndex),
        defaultGeometryKind: item.defaultGeometryKind as GeometryKind,
        allowedGeometryKinds: item.allowedGeometryKinds as GeometryKind[],
        defaultColour: item.defaultColour ?? '#8d6e63',
        methodIds: item.methodIds,
      });
    } else {
      setEditingItem(null);
      setForm(emptyForm);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setForm(emptyForm);
  };

  const toggleAllowedKind = (kind: GeometryKind) => {
    setForm((prev) => {
      const has = prev.allowedGeometryKinds.includes(kind);
      // Never allow removing the current default kind — it must stay selected.
      if (has && kind === prev.defaultGeometryKind) return prev;
      const allowedGeometryKinds = has
        ? prev.allowedGeometryKinds.filter((k) => k !== kind)
        : [...prev.allowedGeometryKinds, kind];
      return { ...prev, allowedGeometryKinds };
    });
  };

  const handleSubmit = async () => {
    try {
      const url = editingItem ? `/api/operation-types/${editingItem.id}` : '/api/operation-types';
      const method = editingItem ? 'PUT' : 'POST';
      const payload = {
        name: form.name.trim(),
        alias: form.alias.trim() || null,
        sortIndex: Number(form.sortIndex),
        defaultGeometryKind: form.defaultGeometryKind,
        allowedGeometryKinds: form.allowedGeometryKinds,
        defaultColour: form.defaultColour || null,
        methodIds: form.methodIds,
      };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const fieldErrors = errorData.errors ? Object.values(errorData.errors).flat() : [];
        throw new Error(errorData.error || fieldErrors.join(' ') || 'Failed to save operation type');
      }

      handleCloseDialog();
      await fetchAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleArchive = async (id: string) => {
    if (!window.confirm('Archive this operation type?')) return;
    try {
      const response = await fetch(`/api/operation-types/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to archive operation type');
      await fetchAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleRestore = async (id: string) => {
    try {
      const response = await fetch(`/api/operation-types/${id}/restore`, { method: 'POST', headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to restore operation type');
      await fetchAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const isFormValid = form.name.trim().length > 0 && form.allowedGeometryKinds.includes(form.defaultGeometryKind);

  if (loading && items.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1.5} mb={2}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <AgricultureIcon />
          Operation Type Management
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <FormControlLabel
            control={<Switch checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />}
            label="Show Archived"
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add Operation Type
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {items.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No operation types found.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ '& .MuiTableCell-root': { py: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Default Shape</TableCell>
                <TableCell>Allowed Shapes</TableCell>
                <TableCell>Methods</TableCell>
                <TableCell>Colour</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.name}
                    {item.alias && item.alias !== item.name && (
                      <Typography variant="caption" color="text.secondary" display="block">{item.alias}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{item.defaultGeometryKind}</TableCell>
                  <TableCell>{item.allowedGeometryKinds.join(', ')}</TableCell>
                  <TableCell>
                    {item.methodIds.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">None</Typography>
                    ) : (
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {item.methodIds.map((id) => (
                          <Chip key={id} label={methodName(id)} size="small" sx={{ height: 20, fontSize: '0.6875rem' }} />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.defaultColour && (
                      <Box sx={{ width: 20, height: 20, borderRadius: '4px', bgcolor: item.defaultColour, border: '1px solid rgba(0,0,0,0.2)' }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {item.archivedDate ? (
                      <Chip label="Archived" size="small" color="default" />
                    ) : (
                      <Chip label="Active" size="small" color="success" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {item.archivedDate ? (
                      <Tooltip title="Restore">
                        <IconButton size="small" onClick={() => handleRestore(item.id)}>
                          <RestoreIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenDialog(item)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Archive">
                          <IconButton size="small" color="error" onClick={() => handleArchive(item.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Operation Type' : 'Add New Operation Type'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              required
              helperText='e.g. "Spraying"'
            />
            <TextField
              fullWidth
              label="Alias"
              value={form.alias}
              onChange={(e) => setForm((prev) => ({ ...prev, alias: e.target.value }))}
              helperText="Optional short label"
            />
            <TextField
              fullWidth
              label="Sort Index"
              type="number"
              value={form.sortIndex}
              onChange={(e) => setForm((prev) => ({ ...prev, sortIndex: e.target.value }))}
            />

            <FormControl fullWidth>
              <InputLabel id="default-geometry-kind-label">Default Shape</InputLabel>
              <Select
                labelId="default-geometry-kind-label"
                label="Default Shape"
                value={form.defaultGeometryKind}
                onChange={(e) => {
                  const kind = e.target.value as GeometryKind;
                  setForm((prev) => ({
                    ...prev,
                    defaultGeometryKind: kind,
                    allowedGeometryKinds: prev.allowedGeometryKinds.includes(kind)
                      ? prev.allowedGeometryKinds
                      : [...prev.allowedGeometryKinds, kind],
                  }));
                }}
              >
                {GEOMETRY_KINDS.map((k) => (
                  <MenuItem key={k} value={k}>{k}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Allowed Shapes — which drawing tools a Record of this type may use
              </Typography>
              <FormGroup row>
                {GEOMETRY_KINDS.map((k) => (
                  <FormControlLabel
                    key={k}
                    control={
                      <Checkbox
                        checked={form.allowedGeometryKinds.includes(k)}
                        onChange={() => toggleAllowedKind(k)}
                        disabled={k === form.defaultGeometryKind}
                      />
                    }
                    label={k}
                  />
                ))}
              </FormGroup>
            </Box>

            <TextField
              fullWidth
              label="Default Colour"
              type="color"
              value={form.defaultColour}
              onChange={(e) => setForm((prev) => ({ ...prev, defaultColour: e.target.value }))}
              sx={{ '& input': { height: 40, cursor: 'pointer' } }}
            />

            <Autocomplete
              multiple
              options={methods.filter((m) => !m.archivedDate)}
              getOptionLabel={(m) => m.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={methods.filter((m) => form.methodIds.includes(m.id))}
              onChange={(_, value) => setForm((prev) => ({ ...prev, methodIds: value.map((m) => m.id) }))}
              renderInput={(params) => (
                <TextField {...params} label="Allowed Methods" placeholder="Select methods…" />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!isFormValid}>
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperationTypeManagement;
